import React from "react";

import { errorToast, useToast } from "../../../components";
import { useSendGAEvent } from "../../../utils/googleAnalytics";
import {
  AddNewCallNoteMutation,
  CallFragment,
  CallNotesV2Query,
  CallNoteType,
  CallNoteVisibility,
  useCallNotesV2LazyQuery,
} from "../../graphql";
import useAddCallNote from "../../graphql/hooks/useAddCallNote";
import useAutofill, { getAts } from "../../hooks/useAutofill";
import { useCopyNotes } from "../../hooks/useCopyNotes";
import useCurrentUser from "../../hooks/useCurrentUser";
import CopyNotesButtons from "../CallNotes/CopyNotesButtons";
import { NotesV2TabProps } from "../Recording/SidebarTabs/NotesV2Tab";

type CallNotesParams = {
  call: Pick<
    CallFragment,
    | "id"
    | "greenhouseScorecardLinkWithId"
    | "leverInterviewLinkWithId"
    | "ashbyFeedbackLinkWithId"
    | "candidate"
    | "position"
    | "interviewerIds"
  >;
  onCallNotesV2QueryCompleted?: (data: CallNotesV2Query) => void;
  onAddCallNoteCompleted?: (data: AddNewCallNoteMutation) => void;
  disableAutofill?: boolean;
  notesForCurrentUserOnly: boolean;
};

type CallNotesReturn = Pick<
  NotesV2TabProps,
  "questions" | "generalNotes" | "onAddNote" | "headerSlot" | "error"
> & { getCallNotesV2(): void; loading: boolean };

const useCallNotesV2 = ({
  call,
  onCallNotesV2QueryCompleted,
  onAddCallNoteCompleted,
  disableAutofill,
  notesForCurrentUserOnly,
}: CallNotesParams): CallNotesReturn => {
  const currentUser = useCurrentUser();
  const sendGAEvent = useSendGAEvent();
  const toast = useToast();
  const callId = call.id;
  const [
    getCallNotesV2,
    { data: notesData, loading: notesLoading, error: notesError },
  ] = useCallNotesV2LazyQuery({
    variables: {
      callId,
      notesForCurrentUserOnly,
    },
    onCompleted: onCallNotesV2QueryCompleted,
  });

  const [addCallNote] = useAddCallNote({
    call: { id: callId, __typename: "Call" },
    onCompleted: onAddCallNoteCompleted,
    onError() {
      errorToast(toast, "There was a problem adding your note");
    },
  });

  const questions = notesData?.call?.questions ?? [];
  const generalNotes = notesData?.call?.generalNotes ?? [];
  const ats = getAts(call);

  const { autofillEnabled, onAutofill } = useAutofill(call);
  const handleCopyNotes = useCopyNotes({ call, generalNotes, questions });

  const isInterviewer = call.interviewerIds.includes(currentUser.id);

  return {
    getCallNotesV2,
    generalNotes,
    questions,
    loading: notesData === undefined,
    error: notesError,

    headerSlot: notesLoading ? null : (
      <CopyNotesButtons
        ats={ats}
        onCopy={handleCopyNotes}
        onAutofill={onAutofill}
        autofillEnabled={autofillEnabled && !disableAutofill && isInterviewer}
      />
    ),

    onAddNote: async ({ text, type, time, questionId }) => {
      if (
        text.trim() === "" &&
        [CallNoteType.Note, CallNoteType.Comment].includes(type)
      ) {
        return;
      }
      sendGAEvent("call_note_added", "call_review");
      await addCallNote({
        variables: {
          callId,
          text,
          type,
          time,
          questionId,
          visibility: CallNoteVisibility.Public,
        },
      });
    },
  };
};

export default useCallNotesV2;
